import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Banking Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page of its own.** `focus()` in `content/reports.ts`
 * writes every Industry Focus entry from one sentence template, and Banking was
 * one of the sixteen still carrying it: a two-paragraph series description under
 * an amber note saying the sector's own words had never been supplied. They were
 * supplied on 2026-08-27, *"banking focus report"*, with the same five
 * attachments the Automotive cut arrived with hours earlier — two screenshots of
 * digitopia.co's Banking Industry Focus page as the reference, the cover at
 * 2560px, Figure 1 and the report mockup. So this is the eighth designed report
 * page and the third focus cut to arrive with **both** of its figures as files.
 *
 * **Where the words come from, band by band.** Everything here is transcribed
 * from reference captures of the live page. The first pair, on 2026-08-27,
 * rendered body copy down to a few pixels, so the *shape* of the page was certain
 * and every word was a careful reading — which is why each band carried a
 * `check`. The page was re-sent on 2026-08-28, *"birebir tıpa tıp aynısını
 * tasarla"*, as four legible full-width slices of the same page, and those are
 * what the copy below now says word for word. Three of the four notes are
 * therefore resolved rather than restated: they asked to be read back against
 * digitopia.co, and this is that reading.
 *
 * **British spelling went with them.** The first transcription set this page in
 * -ise/-isation while its fifteen sibling cuts (`content/daimiAutomotive.ts`,
 * `daimiRetail.ts`, `daimiServices.ts` …) and the shared closing band are all
 * -ize/-ization, which is also what the capture prints. Banking was the odd one
 * out in both directions at once; it is not now.
 *
 * **The `**` in three of these strings is a bold run**, set by `Rich` in
 * `pages/DaimiBanking.tsx`. The reference bolds a phrase inside a sentence three
 * times and a plain string cannot carry that. `body[0]` — the one string
 * `prerender.tsx` lifts into the meta description — carries no marker.
 *
 * **What the live page has that this page cannot.** Its five Download buttons
 * open an embedded HubSpot form. These documents are prerendered with no client
 * bundle at all (see `src/prerender.tsx`), so an embedded form script would never
 * run — and the other reports on this site hand the reader the PDF from our own
 * bucket instead. So every Download button here is that PDF, and
 * `DAIMI_BK_DOWNLOAD.check` is what keeps the question of gating open.
 */

/**
 * The report this page belongs to — the branch `pages/Reports.tsx` takes on it.
 *
 * Note the slug shape: Banking's live URL puts the year before the sector
 * (`…-report-2026-banking-industry`) where Automotive's puts it after
 * (`…-report-automotive-2026`). Both are what digitopia.co serves, so neither is
 * tidied — this is the slug the report has always been listed under here, and
 * changing it would break the inbound links this page exists to keep working.
 */
export const DAIMI_BK_SLUG = 'digital-and-ai-maturity-report-2026-banking-industry'

/**
 * The hero's three paragraphs, read off the reference screenshot.
 *
 * The third is `FOCUS_CLOSE` in `reports.ts` word for word — the series' own
 * closing line, which that module took from the live Airlines page in the first
 * place, and which the live Banking page closes its hero with too. Written out
 * rather than imported so that rewording the series line for the other fifteen
 * cuts cannot silently reword the page it appears on.
 */
export const DAIMI_BK_INTRO = {
  body: [
    'The banking industry leads the 2026 DAIMI benchmark with a score of 3.35, driven by years of disciplined digitization, strong governance, and data-driven operating models.',
    'However, in an era of rapid AI acceleration, maturity has transitioned from a strategic metric to a fundamental **survival capability**. Future success depends on moving beyond “AI FOMO” to scale solutions responsibly.',
    'This report outlines the sector’s maturity, leading strategies, and actions to accelerate transformation.',
  ],
  /**
   * The report cover, at the 2560px size supplied on 2026-08-27 and re-sent with
   * the 2026-08-28 captures. It sits in the hero because the legible capture
   * shows it there — the earlier low-resolution pair read as an empty right-hand
   * column, and `DAIMI_BK_INTRO.check` used to ask about exactly that. It does
   * not any more.
   */
  photo: {
    src: '/photo/daimi-report-2026-banking-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Banking Focus, February 2026',
  } satisfies Photo,
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The live band is a lede paragraph followed by two lines that each open with a
 * bolded label — "A Widening Gap:", "Industrialisation vs. Experimentation:".
 * Split into `label` and `body` here so the label can be set rather than
 * emphasised inside a sentence, which is what a `<strong>` in the middle of a
 * paragraph gives. Two points, not the three the Automotive cut has — this
 * sector's band is genuinely shorter.
 */
export const DAIMI_BK_FINDINGS = {
  heading: 'DAIMI Findings',
  lede: 'Banking’s score of 3.26 reflects a sector matured by necessity, excelling in customer-facing digitization, process discipline, and risk management. This foundation enables reliable execution as the industry navigates the high-stakes shift toward AI transformation.',
  points: [
    {
      label: 'A Widening Gap',
      body: 'A significant divide is emerging between “breakaway leaders” and those stalled by legacy constraints or unbalanced maturity.',
    },
    {
      label: 'Industrialization vs. Experimentation',
      body: 'Winners are successfully industrializing AI, while others remain trapped in endless pilots and policy debates.',
    },
  ],
  /**
   * Figure 1 itself, which is the file supplied with this request — the same
   * chart type the Automotive cut carries, not the Airlines vitality scatter.
   * Six years of DAIMI results, 2020 to 2025, each year a range from the laggard
   * through the average to the leader, with the company count for that year in
   * the header. It is 1200×645 on a white ground, so it gets a white card of its
   * own and a floor under it — six stacked ranges with three labelled values each
   * do not survive being squeezed to phone width.
   */
  photo: {
    src: '/photo/daimi-banking-study-results.webp',
    alt: 'DAIMI study results for the banking industry, 2020 to 2025: the laggard, average and leader score each year, from 2.20–3.93 in 2020 to 2.53–4.10 in 2025',
  } satisfies Photo,
  caption:
    'Figure 1: Digital & AI Maturity Index (DAIMI) Study Results: Banking Industry Score, Averages, Leaders, and Laggards',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  check:
    'One number, stated three ways on the page you sent. This lede says banking scored 3.26 — that is what the capture prints and it is what this band now says. The hero two bands above says 3.35, and Figure 1, which is your own file, labels the 2025 average 3.35 as well. So the live page disagrees with itself and with its own chart. Both figures are copied faithfully rather than quietly reconciled, because which one is right is a question about the study, not about the page. Say which, and one edit settles both bands.',
}

/** The analysis band, with the report mockup beside it. */
export const DAIMI_BK_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Banking',
  body: [
    'To lead in 2026, banking executives must move beyond “more technology” and focus on **more discipline**. Success requires an aggressive investment in people and culture to ensure that automation and AI-driven insights are actually integrated into daily work habits.',
    'Download the full report to access the detailed DAIMI benchmarks and a strategic roadmap designed to help your organization navigate **AI FOMO** and turn digital effort into repeatable, scaled capability.',
  ],
  /** The mockup supplied with this request — the banking cover, not another cut's. */
  photo: {
    src: '/photo/daimi-report-2026-banking-mockup.webp',
    alt: 'Two copies of the Digital & AI Maturity Report 2026 Banking Focus',
  } satisfies Photo,
}

/*
 * The methodology and global-perspective bands used to be written out here, each
 * with its own transcription of copy that is the same on every focus cut — and
 * this page is the one that proved they drift, reading "1,500+ assessments"
 * where Airports and Airlines read "1,300+". They are `content/reportClosing.ts`
 * now, drawn by `components/ReportClosing.tsx` on every report page but three —
 * asked for on 2026-08-28, *"her report sayfamızın en altında bu tasarım bire bir
 * aynı şekilde olmalı"*. The count question is asked once there.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_BK_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Banking Industry Focus cut of the Digital & AI Maturity Report 2026.',
  check:
    'The fourth capture shows this band in full for the first time: a gated HubSpot form — First Name, Last Name, Business Email, Company Name, Job Title, Phone Number, Country, Industry, a marketing-consent checkbox, the privacy statement, and a “Download Now” button. It is not built here. These report pages are prerendered with no JavaScript at all (`src/prerender.tsx`), so an embedded form script would never run and a form that cannot submit is worse than no form; every Download button on this page hands over the PDF from our own bucket instead, which is what the other reports on this site do. Gating it needs an engineer to give the site a form endpoint. Say if that is wanted and it becomes a piece of work rather than a note.',
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiChecks()` and `daimiAutomotiveChecks()`
 * use, and appended to the same list in `reportChecks()`.
 *
 * `'content'` rather than `'asset'`: nothing here is a missing file — this cut
 * arrived with both of its figures. Two notes rather than the four this page
 * carried before 2026-08-28. The three that asked for a reading against
 * digitopia.co got it — the legible captures are the answer, and the copy above
 * now says what they print — so what is left is the one thing reading them more
 * closely *created* (the live page scores banking 3.26 in one band and 3.35 in
 * two others) and the one thing no reading can settle (whether the download
 * should be gated behind the form the site cannot run).
 */
export function daimiBankingChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'findings', check: DAIMI_BK_FINDINGS.check },
    { section: 'download', check: DAIMI_BK_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_BK_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
